import NoSearchResults from "./no-search-results.jsx";
import EmptyState from "./empty-state.jsx";
import { apiFetch } from "./app-state.jsx";
import React from "react";
import "./admin.css";
import logo from "./assets/hallef-logo-tight.png";

function getApiUrl() {
  return process.env.REACT_APP_API_URL?.replace(/\/+$/, "");
}

function formatDate(value) {
  if (!value) return "";
  return new Date(value).toISOString().slice(0, 10);
}

function getResumeHref(application) {
  const resume = application?.resumeUrl || "";
  if (!resume) return "";
  if (resume.startsWith("data:") || resume.startsWith("http")) return resume;

  const name = application?.resumeName || "";
  const type = name.toLowerCase().endsWith(".docx")
    ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    : name.toLowerCase().endsWith(".doc")
      ? "application/msword"
      : "application/pdf";

  return `data:${type};base64,${resume}`;
}

function stripHtml(value) {
  const doc = new DOMParser().parseFromString(String(value || ""), "text/html");
  return doc.body.textContent || "";
}

const RichTextEditor = React.forwardRef(function RichTextEditor({ value, onChange }, ref) {
  const editorRef = React.useRef(null);

  React.useImperativeHandle(ref, () => ({
    getHtml() {
      return editorRef.current?.innerHTML || "";
    },
  }));

  React.useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || "";
    }
  }, [value]);

  function runCommand(command, argument = null) {
    editorRef.current?.focus();
    document.execCommand(command, false, argument);
    onChange(editorRef.current?.innerHTML || "");
  }

  function handleInput() {
    onChange(editorRef.current?.innerHTML || "");
  }

  return (
    <div className="rich-editor">
      <div className="rich-toolbar" aria-label="JD editor toolbar">
        <button type="button" onClick={() => runCommand("formatBlock", "h3")}>H</button>
        <button type="button" onClick={() => runCommand("bold")}>B</button>
        <button type="button" onClick={() => runCommand("italic")}>I</button>
        <button type="button" onClick={() => runCommand("insertUnorderedList")}>•</button>
        <button type="button" onClick={() => runCommand("insertOrderedList")}>1.</button>
        <button type="button" onClick={() => runCommand("formatBlock", "p")}>P</button>
        <button type="button" onClick={() => runCommand("removeFormat")}>Clear</button>
      </div>
      <div
        ref={editorRef}
        className="rich-editor-surface"
        contentEditable
        role="textbox"
        aria-label="Job description"
        data-placeholder="Add full JD: responsibilities, skills, qualification, candidate profile..."
        onInput={handleInput}
        onBlur={handleInput}
      />
    </div>
  );
});

function AdminIcon({ name }) {
  const paths = {
    view: <><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" /><circle cx="12" cy="12" r="2.7" /></>,
    edit: <><path d="m4 16.5-.8 4.3 4.3-.8L18.7 8.8l-3.5-3.5L4 16.5Z" /><path d="m14.2 6.3 3.5 3.5" /></>,
    delete: <><path d="M4 7h16" /><path d="M9 7V4h6v3" /><path d="m6 7 1 14h10l1-14" /><path d="M10 11v6" /><path d="M14 11v6" /></>,
    plus: <><path d="M12 5v14" /><path d="M5 12h14" /></>,
    clear: <><path d="M6 6l12 12" /><path d="M18 6 6 18" /></>,
    reset: <><path d="M4 7v5h5" /><path d="M20 17a8 8 0 0 1-14.8-4" /><path d="M20 17v-5h-5" /><path d="M4 7a8 8 0 0 1 14.8 4" /></>,
  };

  return (
    <svg className="admin-button-icon" viewBox="0 0 24 24" aria-hidden="true">
      {paths[name]}
    </svg>
  );
}

function Sidebar({ active, setActive }) {
  const items = [
    "Dashboard",
    "Services",
    "Career/Hiring",
    "Inquiries",
    "Logout",
  ];

  function handleLogout() {
    localStorage.removeItem("hallef-admin-token");
    localStorage.removeItem("hallef-admin-auth");
    localStorage.removeItem("hallef-user-role");
    window.location.href = "/login";
  }

  return (
    <aside className="admin-sidebar">
      <a
        className="admin-logo"
        href="/"
        aria-label="Hallef Technologies home"
      >
        <img src={logo} alt="Hallef Technologies" />
      </a>

      <nav aria-label="Admin navigation">
        {items.map((item) => (
          <button
            className={`admin-nav-button${active === item ? " is-active" : ""}`}
            aria-current={active === item ? "page" : undefined}
            type="button"
            onClick={() => {
              if (item === "Logout") {
                handleLogout();
              } else {
                setActive(item);
              }
            }}
            key={item}
          >
            {item}
          </button>
        ))}
      </nav>
    </aside>
  );
}

function Dashboard({ jobs, inquiries, serviceProjects, applications }) {
  return (
    <section className="admin-section">
      <div className="section-head">
        <div>
          <p>Overview</p>
          <h1>Dashboard</h1>
        </div>

        <span className="admin-date">Today</span>
      </div>

      <div className="metric-grid">
        <article>
          <span>Open Jobs</span>
          <strong>
            {jobs.filter((job) => job.status === "Open").length}
          </strong>
        </article>

        <article>
          <span>Applications</span>
          <strong>{applications.length}</strong>
        </article>

        <article>
          <span>New Inquiries</span>
          <strong>
            {inquiries.filter((item) => item.status === "New").length}
          </strong>
        </article>

        <article>
          <span>Active Services</span>
          <strong>{serviceProjects.length}</strong>
        </article>
      </div>

      {/* <div className="dashboard-panel">
        <h2>Admin Focus</h2>

        <p>
          Manage services, brand visibility, hiring activity and customer
          inquiries from one clean workspace.
        </p>
      </div> */}
    </section>
  );
}

function ServicesPage({ serviceProjects, setServiceProjects }) {
  const [tab, setTab] = React.useState("Services");

  const [isDragging, setIsDragging] = React.useState(false);

  const emptyForm = {
    id: "",
    name: "",
    meta: "",
    title: "",
    body: "",
    image: "",
    note: "",
    websiteUrl: "",
  };

  const [form, setForm] = React.useState(emptyForm);
  const [formErrors, setFormErrors] = React.useState({});

  function validateServiceForm(formValues) {
    const nextErrors = {};

    if (!String(formValues.name || "").trim()) {
      nextErrors.name = "Service / brand name is required.";
    }

    if (!String(formValues.meta || "").trim()) {
      nextErrors.meta = "Meta label is required.";
    }

    if (!String(formValues.title || "").trim()) {
      nextErrors.title = "Title is required.";
    }

    if (!String(formValues.image || "").trim()) {
      nextErrors.image = "Image is required.";
    }

    if (!String(formValues.body || "").trim()) {
      nextErrors.body = "Description is required.";
    }

    return nextErrors;
  }

  function updateForm(event) {
    const nextForm = {
      ...form,
      [event.target.name]: event.target.value,
    };

    setForm(nextForm);

    if (formErrors[event.target.name]) {
      setFormErrors(validateServiceForm(nextForm));
    }
  }

  function readImageFile(file) {
    if (!file || !file.type.startsWith("image/")) return;

    const reader = new FileReader();

    reader.onload = () => {
      setForm((current) => ({
        ...current,
        image: reader.result,
      }));
      setFormErrors((current) => {
        const nextErrors = { ...current };
        delete nextErrors.image;
        return nextErrors;
      });
    };

    reader.readAsDataURL(file);
  }

  function handleUpload(event) {
    readImageFile(event.target.files[0]);
    event.target.value = "";
  }

  function handleDrop(event) {
    event.preventDefault();

    setIsDragging(false);

    readImageFile(event.dataTransfer.files[0]);
  }

  function resetForm() {
    setForm(emptyForm);
    setFormErrors({});
  }

  function persistProjects(nextProjects) {
    setServiceProjects(nextProjects);
  }

  async function submitService(event) {
    event.preventDefault();

    const nextErrors = validateServiceForm(form);
    setFormErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    if (form.id) {
      const apiUrl = getApiUrl();
      const currentProject = serviceProjects.find((project) => String(project.id || project.name) === String(form.id));
      const nextProject = {
        ...currentProject,
        ...form,
        id: currentProject?.id || form.id,
        number: currentProject?.number || "01",
        meta: form.meta || "SERVICE",
        image: form.image || currentProject?.image,
        // note: form.note || "Plan / Build / Scale",
        websiteUrl: form.websiteUrl || "",
        mark: currentProject?.mark || "stay",
        layout: currentProject?.layout || "copy-first",
        callout: currentProject?.callout || form.note || "",
      };

      if (apiUrl && currentProject?.id && !Number.isNaN(Number(currentProject.id))) {
        try {
          const response = await apiFetch(`${apiUrl}/brands/${currentProject.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(nextProject),
          });

          if (response.ok) {
            const updated = await response.json();
            persistProjects(
              serviceProjects.map((project) =>
                project.id === currentProject.id ? updated : project
              )
            );
            resetForm();
            return;
          }
        } catch (error) {
          console.error("ADMIN UPDATE SERVICE ERROR:", error);
        return;
        }
      }

      persistProjects(
        serviceProjects.map((project) =>
          (project.id || project.name) === form.id
            ? nextProject
            : project
        )
      );

      resetForm();
      return;
    }

    const nextProject = {
      ...form,
      id: String(Date.now()),
      number: String(serviceProjects.length + 1).padStart(2, "0"),
      meta: form.meta || "SERVICE",
      image:
        form.image ||
        "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1200&q=85",
      note: form.note || "Plan / Build / Scale",
      websiteUrl: form.websiteUrl || "",
      mark: "stay",
      layout:
        serviceProjects.length % 2 === 1
          ? "image-first"
          : "copy-first",
    };

    const apiUrl = getApiUrl();
    if (apiUrl) {
      try {
        const response = await apiFetch(`${apiUrl}/brands`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(nextProject),
        });

        if (response.ok) {
          const created = await response.json();
          persistProjects([
            ...serviceProjects,
            created,
          ]);
          resetForm();
          return;
        }
      } catch (error) {
        console.error("ADMIN CREATE SERVICE ERROR:", error);
        return;
      }
    }

    persistProjects([
      ...serviceProjects,
      nextProject,
    ]);

    resetForm();
  }

  function editService(project) {
    setForm({
      id: project.id || project.name,
      name: project.name,
      meta: project.meta,
      title: project.title,
      body: project.body,
      image: project.image,
      note: project.note,
      websiteUrl: project.websiteUrl || "",
    });

    setTab("Services");
  }

  async function deleteService(projectId) {
    const apiUrl = getApiUrl();
    const currentProject = serviceProjects.find(
      (project) =>
        String(project.id || project.name) === String(projectId)
    );

    if (apiUrl && currentProject?.id && !Number.isNaN(Number(currentProject.id))) {
      try {
        await apiFetch(`${apiUrl}/brands/${currentProject.id}`, { method: "DELETE" });
      } catch (error) {
        console.error("ADMIN DELETE SERVICE ERROR:", error);
        return;
      }
    }

    const nextProjects = serviceProjects
      .filter(
        (project) =>
          (project.id || project.name) !== projectId
      )
      .map((project, index) => ({
        ...project,
        number: String(index + 1).padStart(2, "0"),
      }));

    persistProjects(nextProjects);
  }

  function clearServices() {
    persistProjects([]);
    resetForm();
  }

  return (
    <section className="admin-section">
      <div className="section-head">
        <div>
          <p>Manage Website</p>
          <h1>Services</h1>
        </div>

        <div className="tabs">
          {["Services", "Brands"].map((item) => (
            <button
              className={
                tab === item ? "is-active" : ""
              }
              type="button"
              onClick={() => setTab(item)}
              key={item}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      {tab === "Services" ? (
        <>
          <form
            className="service-manage-form"
            onSubmit={submitService}
            noValidate
          >
            <label className="admin-field">
              <input
                className={formErrors.name ? "has-error" : ""}
                name="name"
                value={form.name}
                onChange={updateForm}
                placeholder="Service / brand name"
                aria-invalid={Boolean(formErrors.name)}
              />
              {formErrors.name && <span className="admin-field-error">{formErrors.name}</span>}
            </label>

            <label className="admin-field">
              <input
                className={formErrors.meta ? "has-error" : ""}
                name="meta"
                value={form.meta}
                onChange={updateForm}
                placeholder="Meta label"
                aria-invalid={Boolean(formErrors.meta)}
              />
              {formErrors.meta && <span className="admin-field-error">{formErrors.meta}</span>}
            </label>

            <label className="admin-field">
              <input
                className={formErrors.title ? "has-error" : ""}
                name="title"
                value={form.title}
                onChange={updateForm}
                placeholder="Title"
                aria-invalid={Boolean(formErrors.title)}
              />
              {formErrors.title && <span className="admin-field-error">{formErrors.title}</span>}
            </label>

            <label className="admin-field">
              <input
                className={formErrors.image ? "has-error" : ""}
                name="image"
                value={form.image}
                onChange={updateForm}
                placeholder="Image URL"
                aria-invalid={Boolean(formErrors.image)}
              />
              {formErrors.image && <span className="admin-field-error">{formErrors.image}</span>}
            </label>

            <input
              name="websiteUrl"
              value={form.websiteUrl}
              onChange={updateForm}
              placeholder="Project website URL"
            />

            <label
              className={`upload-dropzone ${
                isDragging ? "is-dragging" : ""
              } ${formErrors.image ? "has-error" : ""}`}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleUpload}
              />

              <span>
                {form.image
                  ? "Image selected"
                  : "Drop image here or upload"}
              </span>
            </label>

            <label className="admin-field admin-field-wide">
              <textarea
                className={formErrors.body ? "has-error" : ""}
                name="body"
                value={form.body}
                onChange={updateForm}
                placeholder="Description"
                aria-invalid={Boolean(formErrors.body)}
              />
              {formErrors.body && <span className="admin-field-error">{formErrors.body}</span>}
            </label>

            <input
              name="note"
              value={form.note}
              onChange={updateForm}
              placeholder="Visual note"
            />

            <button type="submit">
              <AdminIcon name="plus" />
              {form.id
                ? "Update Service"
                : "Add Service"}
            </button>

            <button
              type="button"
              onClick={resetForm}
            >
              <AdminIcon name="clear" />
              Clear
            </button>

            <button
              type="button"
              onClick={clearServices}
            >
              <AdminIcon name="reset" />
              Clear Services
            </button>
          </form>

          <div className="service-editor-list">
            {serviceProjects.map((project) => (
              <article
                className="service-editor-card"
                key={project.id || project.name}
              >
                <figure>
                  <img
                    src={project.image}
                    alt=""
                  />

                  <figcaption>
                    {project.note}
                  </figcaption>
                </figure>

                <div>
                  <span>{project.number}</span>

                  <p className="brand-meta">
                    {project.meta}
                  </p>

                  <h2>{project.title}</h2>

                  <p>{project.body}</p>

                  <div className="card-actions">
                    <button
                      type="button"
                      onClick={() =>
                        editService(project)
                      }
                    >
                      <AdminIcon name="edit" />
                      Edit Content
                    </button>

                    <button
                      type="button"
                      className="admin-icon-only"
                      aria-label="Delete service"
                      title="Delete"
                      onClick={() =>
                        deleteService(
                          project.id ||
                            project.name
                        )
                      }
                    >
                      <AdminIcon name="delete" />
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </>
      ) : (
        <div className="brand-grid">
          {serviceProjects.map((project) => (
            <article
              className="brand-card"
              key={project.id || project.name}
            >
              <div className="brand-top">
                <span>{project.number}</span>
                <strong>{project.name}</strong>
              </div>

              <p className="brand-meta">
                {project.meta}
              </p>

              <h2>{project.title}</h2>

              <p>{project.body}</p>

              <button
                type="button"
                onClick={() =>
                  editService(project)
                }
              >
                <AdminIcon name="edit" />
                Edit Brand
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function CareersPage({ jobs, setJobs, applications, setApplications }) {
  const [form, setForm] = React.useState({
    id: "",
    title: "",
    team: "",
    location: "",
    description: "",
    status: "Open",
  });
  const [selectedApplication, setSelectedApplication] =
    React.useState(null);
  const jdEditorRef = React.useRef(null);
  const [jobSaveStatus, setJobSaveStatus] = React.useState("");

  function updateForm(event) {
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }));
  }

  function updateDescription(description) {
    setForm((current) => ({
      ...current,
      description,
    }));
  }

  function resetJobForm() {
    setForm({
      id: "",
      title: "",
      team: "",
      location: "",
      description: "",
      status: "Open",
    });
  }

  function editJob(job) {
    setForm({
      id: job.id || "",
      title: job.title || "",
      team: job.team || "",
      location: job.location || "",
      description: job.description || "",
      status: job.status || "Open",
    });
  }

  async function addJob(event) {
    event.preventDefault();
    setJobSaveStatus("");

    if (
      !form.title.trim() ||
      !form.team.trim() ||
      !form.location.trim()
    ) {
      return;
    }

    const apiUrl = getApiUrl();
    const currentDescription = jdEditorRef.current?.getHtml() || form.description || "";
    const payload = {
      title: form.title.trim(),
      team: form.team.trim(),
      location: form.location.trim(),
      description: currentDescription,
      status: form.status,
    };

    if (apiUrl) {
      try {
        const response = await apiFetch(
          form.id ? `${apiUrl}/careers/jobs/${form.id}` : `${apiUrl}/careers/jobs`,
          {
            method: form.id ? "PATCH" : "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );

        if (response.ok) {
          const saved = await response.json();
          setJobs(
            form.id
              ? jobs.map((job) => job.id === saved.id ? saved : job)
              : [saved, ...jobs]
          );
        } else {
          const data = await response.json().catch(() => ({}));
          setJobSaveStatus(data.message || "Job could not be saved.");
          return;
        }
      } catch (error) {
        console.error("ADMIN SAVE JOB ERROR:", error);
        setJobSaveStatus("Cannot connect to backend while saving job.");
        return;
      }
    } else {
      const saved = {
        ...payload,
        id: form.id || Date.now(),
      };

      setJobs(
        form.id
          ? jobs.map((job) => job.id === form.id ? saved : job)
          : [saved, ...jobs]
      );
    }

    resetJobForm();
    setJobSaveStatus(form.id ? "Job updated successfully." : "Job added successfully.");
  }

  async function deleteJob(id) {
    const apiUrl = getApiUrl();

    if (apiUrl) {
      try {
        await apiFetch(`${apiUrl}/careers/jobs/${id}`, { method: "DELETE" });
      } catch (error) {
        console.error("ADMIN DELETE JOB ERROR:", error);
        return;
      }
    }

    setJobs(jobs.filter((job) => job.id !== id));
  }

  async function updateApplication(id, status) {
    const apiUrl = getApiUrl();

    if (apiUrl) {
      try {
        await apiFetch(`${apiUrl}/careers/applications/${id}/status`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        });
      } catch (error) {
        console.error("ADMIN UPDATE APPLICATION ERROR:", error);
        return;
      }
    }

    setApplications(
      applications.map((item) =>
        item.id === id
          ? {
              ...item,
              status,
            }
          : item
      )
    );
  }

  return (
    <section className="admin-section">
      <div className="section-head">
        <div>
          <p>Hiring</p>
          <h1>Career/Hiring</h1>
        </div>
      </div>

      <form
        className="admin-form"
        onSubmit={addJob}
      >
        <input
          name="title"
          value={form.title}
          onChange={updateForm}
          placeholder="Job title"
        />

        <input
          name="team"
          value={form.team}
          onChange={updateForm}
          placeholder="Team"
        />

        <input
          name="location"
          value={form.location}
          onChange={updateForm}
          placeholder="Location"
        />

        <RichTextEditor ref={jdEditorRef} value={form.description} onChange={updateDescription} />

        <select
          name="status"
          value={form.status}
          onChange={updateForm}
        >
          <option>Open</option>
          <option>Paused</option>
          <option>Closed</option>
        </select>

        <button type="submit">
          {form.id ? "Update Job" : "Add Job"}
        </button>
        {form.id && (
          <button type="button" onClick={resetJobForm}>
            Cancel Edit
          </button>
        )}
        {jobSaveStatus && <p className="admin-form-status">{jobSaveStatus}</p>}
      </form>

      <div className="split-grid">
        <div className="admin-table-card">
          <h2>Job Openings</h2>

          <table className="jobs-table">
            <thead>
              <tr>
                <th>Role</th>
                <th>Team</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {jobs.map((job) => (
                <tr key={job.id}>
                  <td>
                    <strong>{job.title}</strong>
                    <small>{job.location}</small>
                    {job.description && (
                      <small className="job-description-preview">
                        {stripHtml(job.description)}
                      </small>
                    )}
                  </td>

                  <td>{job.team}</td>

                  <td className="job-status-cell">
                    <div className="job-cell-control">
                      <span className="status">
                        {job.status}
                      </span>
                    </div>
                  </td>

                  <td className="job-action-cell">
                    <div className="job-cell-control">
                      <button
                        type="button"
                        aria-label="Edit job"
                        title="Edit"
                        onClick={() =>
                          editJob(job)
                        }
                      >
                        <AdminIcon name="edit" />
                      </button>
                      <button
                        type="button"
                        aria-label="Delete job"
                        title="Delete"
                        onClick={() =>
                          deleteJob(job.id)
                        }
                      >
                        <AdminIcon name="delete" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="admin-table-card">
          <h2>Applications</h2>

          <table className="applications-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {applications.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{[item.firstName, item.lastName].filter(Boolean).join(" ")}</strong>
                    <small>{item.email}</small>
                  </td>

                  <td>{item.role}</td>

                  <td className="application-status-cell">
                    <select
                      value={item.status}
                      onChange={(event) =>
                        updateApplication(
                          item.id,
                          event.target.value
                        )
                      }
                    >
                      <option>Review</option>
                      <option>Shortlisted</option>
                      <option>Rejected</option>
                      <option>Hired</option>
                    </select>
                  </td>

                  <td className="application-action-cell">
                    <button
                      type="button"
                      aria-label="View application"
                      title="View"
                      onClick={() =>
                        setSelectedApplication(item)
                      }
                    >
                      <AdminIcon name="view" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selectedApplication && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() =>
            setSelectedApplication(null)
          }
        >
          <article
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label="Application details"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <button
              className="modal-close"
              type="button"
              aria-label="Close"
              title="Close"
              onClick={() =>
                setSelectedApplication(null)
              }
            />

            <div className="popup-content">
            <h2>{selectedApplication.role}</h2>

            <p>{selectedApplication.message || "No message provided."}</p>

            <dl>
              <dt>Name</dt>
              <dd>{[selectedApplication.firstName, selectedApplication.lastName].filter(Boolean).join(" ")}</dd>

              <dt>Email</dt>
              <dd>{selectedApplication.email}</dd>

              <dt>Phone</dt>
              <dd>{selectedApplication.phone || "-"}</dd>

              <dt>Resume</dt>
              <dd>
                {selectedApplication.resumeUrl ? (
                  <a
                    href={getResumeHref(selectedApplication)}
                    target="_blank"
                    rel="noreferrer"
                    download={selectedApplication.resumeName || undefined}
                  >
                    {selectedApplication.resumeName || "Open Resume"}
                  </a>
                ) : (
                  "-"
                )}
              </dd>

              <dt>Status</dt>
              <dd>{selectedApplication.status}</dd>

              <dt>Date</dt>
              <dd>{formatDate(selectedApplication.createdAt)}</dd>
            </dl>
            </div>
          </article>
        </div>
      )}
    </section>
  );
}

function InquiriesPage({
  inquiries,
  setInquiries,
}) {
  const [filter, setFilter] =
    React.useState("All");

  const [selected, setSelected] =
    React.useState(null);

  const visible =
    filter === "All"
      ? inquiries
      : inquiries.filter(
          (item) => item.status === filter
        );

  async function updateStatus(id, status) {
    const apiUrl = getApiUrl();

    if (apiUrl) {
      try {
        await apiFetch(`${apiUrl}/inquiries/${id}/status`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status }),
        });
      } catch (error) {
        console.error("ADMIN UPDATE INQUIRY ERROR:", error);
        return;
      }
    }

    setInquiries(
      inquiries.map((item) =>
        item.id === id
          ? {
              ...item,
              status,
            }
          : item
      )
    );
  }

  async function deleteInquiry(id) {
    const apiUrl = getApiUrl();

    if (apiUrl) {
      try {
        await apiFetch(`${apiUrl}/inquiries/${id}`, { method: "DELETE" });
      } catch (error) {
        console.error("ADMIN DELETE INQUIRY ERROR:", error);
        return;
      }
    }

    setInquiries(inquiries.filter((item) => item.id !== id));

    setSelected(null);
  }

  return (
    <section className="admin-section">
      <div className="section-head">
        <div>
          <p>Website Messages</p>
          <h1>Inquiries</h1>
        </div>

        <select
          className="filter"
          value={filter}
          onChange={(event) =>
            setFilter(event.target.value)
          }
        >
          <option>All</option>
          <option>New</option>
          <option>In Progress</option>
          <option>Closed</option>
        </select>
      </div>

      <div className="admin-table-card">
        {inquiries.length === 0 ? <EmptyState /> : visible.length === 0 ? <NoSearchResults onReset={() => setFilter("All")} /> : null}
        <table className="inquiry-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Subject</th>
              <th>Message</th>
              <th>Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {visible.map((item) => (
              <tr key={item.id}>
                <td>{item.name}</td>
                <td>{item.email}</td>
                <td>{item.phone}</td>
                <td>{item.subject}</td>
                <td>{item.message}</td>
                <td>{item.date || formatDate(item.createdAt)}</td>

                <td>
                  <span className="status">
                    {item.status}
                  </span>
                </td>

                <td className="table-actions">
                  <button
                    type="button"
                    aria-label="View inquiry"
                    title="View"
                    onClick={() =>
                      setSelected(item)
                    }
                  >
                    <AdminIcon name="view" />
                  </button>

                  <button
                    type="button"
                    aria-label="Edit inquiry status"
                    title="Edit"
                    onClick={() =>
                      updateStatus(
                        item.id,
                        item.status ===
                          "Closed"
                          ? "New"
                          : "Closed"
                      )
                    }
                  >
                    <AdminIcon name="edit" />
                  </button>

                  <button
                    type="button"
                    aria-label="Delete inquiry"
                    title="Delete"
                    onClick={() =>
                      deleteInquiry(item.id)
                    }
                  >
                    <AdminIcon name="delete" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selected && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() =>
            setSelected(null)
          }
        >
          <article
            className="modal"
            role="dialog"
            aria-modal="true"
            aria-label="Inquiry details"
            onClick={(event) =>
              event.stopPropagation()
            }
          >
            <button
              className="modal-close"
              type="button"
              aria-label="Close"
              title="Close"
              onClick={() =>
                setSelected(null)
              }
            />

            <div className="popup-content">
            <h2>{selected.subject}</h2>

            <p>{selected.message}</p>

            <dl>
              <dt>Name</dt>
              <dd>{selected.name}</dd>

              <dt>Email</dt>
              <dd>{selected.email}</dd>

              <dt>Phone</dt>
              <dd>{selected.phone}</dd>

              <dt>Status</dt>
              <dd>{selected.status}</dd>
            </dl>
            </div>
          </article>
        </div>
      )}
    </section>
  );
}

function Admin() {
  const [active, setActive] =
    React.useState("Dashboard");

  const [jobs, setJobs] =
    React.useState([]);

  const [applications, setApplications] =
    React.useState([]);

  const [inquiries, setInquiries] =
    React.useState([]);

  const [serviceProjects, setServiceProjects] =
    React.useState([]);

  React.useEffect(() => {
    const apiUrl = getApiUrl();
    if (!apiUrl) return;

    apiFetch(`${apiUrl}/brands`)
      .then((response) => response.ok ? response.json() : Promise.reject(response))
      .then((data) => {
        if (Array.isArray(data)) {
          setServiceProjects(data);
        }
      })
      .catch((error) => {
        console.error("ADMIN SERVICES LOAD ERROR:", error);
        return;
      });

    apiFetch(`${apiUrl}/careers/jobs`)
      .then((response) => response.ok ? response.json() : Promise.reject(response))
      .then((data) => {
        if (Array.isArray(data)) {
          setJobs(data);
        }
      })
      .catch((error) => {
        console.error("ADMIN JOBS LOAD ERROR:", error);
        return;
      });

    apiFetch(`${apiUrl}/careers/applications`)
      .then((response) => response.ok ? response.json() : Promise.reject(response))
      .then((data) => {
        if (Array.isArray(data)) {
          setApplications(data);
        }
      })
      .catch((error) => {
        console.error("ADMIN APPLICATIONS LOAD ERROR:", error);
        return;
      });

    apiFetch(`${apiUrl}/inquiries`)
      .then((response) => response.ok ? response.json() : Promise.reject(response))
      .then((data) => {
        if (Array.isArray(data)) {
          setInquiries(data);
        }
      })
      .catch((error) => {
        console.error("ADMIN INQUIRIES LOAD ERROR:", error);
        return;
      });
  }, []);

  return (
    <div className="admin-shell">
      <Sidebar
        active={active}
        setActive={setActive}
      />

      <main className="admin-main">
        {active === "Dashboard" && (
          <Dashboard
            jobs={jobs}
            inquiries={inquiries}
            serviceProjects={serviceProjects}
            applications={applications}
          />
        )}

        {active === "Services" && (
          <ServicesPage
            serviceProjects={serviceProjects}
            setServiceProjects={
              setServiceProjects
            }
          />
        )}

        {active === "Career/Hiring" && (
          <CareersPage
            jobs={jobs}
            setJobs={setJobs}
            applications={applications}
            setApplications={setApplications}
          />
        )}

        {active === "Inquiries" && (
          <InquiriesPage
            inquiries={inquiries}
            setInquiries={setInquiries}
          />
        )}
      </main>
    </div>
  );
}

export default Admin;
